import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SeriesHub = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/articles/series/Women Who Changed the Rules`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching series articles:', error);
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-[#2C2C2C] text-white py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-label text-xs text-[#ABBDDF] tracking-widest mb-4 block">SERIES</span>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6" data-testid="series-title">
            Women Who Changed the Rules
          </h1>
          <p className="font-body text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            The women who built things that weren't supposed to exist yet. Who led movements before movements had names. Who hustled — with dignity — inside conditions that were designed for their failure. Their stories are not history lessons. They are blueprints.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="article-card">
                <div className="h-56 loading-skeleton"></div>
                <div className="p-6 space-y-3">
                  <div className="h-4 w-32 loading-skeleton"></div>
                  <div className="h-6 loading-skeleton"></div>
                  <div className="h-4 loading-skeleton"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="series-grid">
            {articles.map((article, index) => (
              <Link 
                key={article.id}
                to={`/en/blog/${article.slug}`}
                className="article-card group"
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={article.hero_image}
                    alt={article.hero_image_alt}
                    className="w-full h-48 sm:h-56 object-cover object-top"
                    loading={index < 6 ? "eager" : "lazy"}
                    width="400"
                    height="224"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#9D6E6D] text-white px-3 py-1 font-label text-xs tracking-widest">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <h2 className="font-heading text-lg sm:text-xl text-[#2C2C2C] mb-3 group-hover:text-[#9D6E6D] transition-colors line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="font-body text-[#767C91] text-sm mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-label text-xs text-[#767C91]">
                      {article.read_time} min read
                    </span>
                    <span className="font-label text-xs text-[#9D6E6D] tracking-widest inline-flex items-center gap-2 group-hover:text-[#2C2C2C] transition-colors">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-[#F2E1E1] py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-[#2C2C2C] mb-4">
            Ready to Write Your Own Story?
          </h2>
          <p className="font-body text-[#767C91] mb-8 max-w-2xl mx-auto">
            Join 1,900+ Lady Hustlers building businesses, careers, and impact together. Access education, community, and tools designed for women who build.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://join.entrepreneurladyhustler.com" target="_blank" rel="noopener noreferrer" className="cta-button">
              Become a Lady Hustler →
            </a>
            <Link to="/en/blog" className="cta-button bg-transparent border border-[#9D6E6D] text-[#9D6E6D] hover:bg-[#9D6E6D] hover:text-white">
              Explore More Articles →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SeriesHub;
